/**
 * Admin Support Tab Component
 * 
 * Admin-facing support ticket management interface.
 * Allows admins to view all tickets, reply, and change status.
 */

import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import {
  listAllTickets,
  getTicketDetailsAdmin,
  adminReply,
  updateTicketStatus,
  type TicketResponse,
  type TicketDetailResponse,
} from "@app/services/supportApi";
import "./admin-support.css";

const ADMIN_SUPPORT_QUERY_KEY = ["admin", "support", "tickets"];

export function AdminSupportTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [filters, setFilters] = useState<{
    status?: string;
    user_id?: string;
  }>({});
  const [replyText, setReplyText] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState<{
    status: string;
    priority?: string;
  }>({ status: "" });
  const [notice, setNotice] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: [...ADMIN_SUPPORT_QUERY_KEY, filters],
    queryFn: () => listAllTickets(filters),
  });

  const { data: ticketDetails, isLoading: isLoadingDetails } = useQuery({
    queryKey: [...ADMIN_SUPPORT_QUERY_KEY, selectedTicketId],
    queryFn: () => {
      if (!selectedTicketId) {
        return null;
      }
      return getTicketDetailsAdmin(selectedTicketId);
    },
    enabled: !!selectedTicketId,
  });

  const replyMutation = useMutation({
    mutationFn: ({ ticketId, message, isInternal }: { ticketId: string; message: string; isInternal: boolean }) =>
      adminReply(ticketId, { message, is_internal: isInternal }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_SUPPORT_QUERY_KEY });
      if (selectedTicketId) {
        queryClient.invalidateQueries({ queryKey: [...ADMIN_SUPPORT_QUERY_KEY, selectedTicketId] });
      }
      setReplyText("");
      setIsInternal(false);
      setNotice({ type: "success", message: "Reply sent successfully." });
    },
    onError: (error: unknown) => {
      setNotice({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to send reply.",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ ticketId, status, priority }: { ticketId: string; status: string; priority?: string }) =>
      updateTicketStatus(ticketId, { status, priority }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_SUPPORT_QUERY_KEY });
      if (selectedTicketId) {
        queryClient.invalidateQueries({ queryKey: [...ADMIN_SUPPORT_QUERY_KEY, selectedTicketId] });
      }
      setStatusUpdate({ status: "" });
      setNotice({ type: "success", message: "Ticket status updated successfully." });
    },
    onError: (error: unknown) => {
      setNotice({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to update status.",
      });
    },
  });

  useEffect(() => {
    if (notice) {
      const timer = setTimeout(() => setNotice(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notice]);

  const handleReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicketId || !replyText.trim()) {
      setNotice({ type: "error", message: "Reply cannot be empty." });
      return;
    }
    replyMutation.mutate({
      ticketId: selectedTicketId,
      message: replyText,
      isInternal,
    });
  };

  const handleStatusUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicketId || !statusUpdate.status) {
      setNotice({ type: "error", message: "Status is required." });
      return;
    }
    updateStatusMutation.mutate({
      ticketId: selectedTicketId,
      status: statusUpdate.status,
      priority: statusUpdate.priority,
    });
  };

  const getStatusPill = (status: string) => {
    const tone: "success" | "warning" | "danger" | "neutral" =
      status === "resolved" || status === "closed"
        ? "success"
        : status === "in_progress"
          ? "warning"
          : status === "open"
            ? "neutral"
            : "danger";
    return <Pill tone={tone}>{status}</Pill>;
  };

  const getPriorityPill = (priority: string) => {
    const tone: "success" | "warning" | "danger" | "neutral" =
      priority === "urgent"
        ? "danger"
        : priority === "high"
          ? "warning"
          : priority === "low"
            ? "success"
            : "neutral";
    return <Pill tone={tone}>{priority}</Pill>;
  };

  return (
    <div className="admin-support-root">
      {notice ? (
        <div className={`admin-support-banner admin-support-banner--${notice.type}`}>
          {notice.message}
        </div>
      ) : null}

      <Card
        title="Support Tickets Management"
        subtitle="View and manage all support tickets from users."
      >
        <div className="admin-support-filters">
          <label>
            Status
            <select
              value={filters.status || ""}
              onChange={(e) =>
                setFilters({ ...filters, status: e.target.value || undefined })
              }
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </label>
          <label>
            User ID
            <input
              type="text"
              value={filters.user_id || ""}
              onChange={(e) =>
                setFilters({ ...filters, user_id: e.target.value || undefined })
              }
              placeholder="Filter by user ID"
            />
          </label>
          <Button
            variant="ghost"
            onClick={() => setFilters({})}
          >
            Clear Filters
          </Button>
        </div>

        {isLoading ? (
          <div className="admin-support-empty">Loading tickets...</div>
        ) : !ticketsData || ticketsData.tickets.length === 0 ? (
          <div className="admin-support-empty">No tickets found.</div>
        ) : (
          <div className="admin-support-tickets-list">
            {ticketsData.tickets.map((ticket) => (
              <div
                key={ticket.id}
                className={`admin-support-ticket-item ${
                  selectedTicketId === ticket.id ? "admin-support-ticket-item--active" : ""
                }`}
                onClick={() => setSelectedTicketId(ticket.id)}
              >
                <div className="admin-support-ticket-item__header">
                  <div>
                    <h4>{ticket.subject}</h4>
                    <div className="admin-support-ticket-item__user">
                      User ID: {ticket.id.split("-")[0]}...
                    </div>
                  </div>
                  <div className="admin-support-ticket-item__badges">
                    {getStatusPill(ticket.status)}
                    {getPriorityPill(ticket.priority)}
                  </div>
                </div>
                <div className="admin-support-ticket-item__meta">
                  <span>Created: {new Date(ticket.created_at).toLocaleString()}</span>
                  <span>Messages: {ticket.message_count}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {selectedTicketId && ticketDetails && (
        <Card title={`Ticket: ${ticketDetails.ticket.subject}`}>
          <div className="admin-support-ticket-details">
            <div className="admin-support-ticket-details__header">
              <div className="admin-support-ticket-details__meta">
                <span>User ID: {ticketDetails.ticket.id}</span>
                <span>Status: {getStatusPill(ticketDetails.ticket.status)}</span>
                <span>Priority: {getPriorityPill(ticketDetails.ticket.priority)}</span>
                <span>Created: {new Date(ticketDetails.ticket.created_at).toLocaleString()}</span>
                {ticketDetails.ticket.resolved_at && (
                  <span>Resolved: {new Date(ticketDetails.ticket.resolved_at).toLocaleString()}</span>
                )}
              </div>
            </div>

            <div className="admin-support-messages">
              <h4>Messages</h4>
              {ticketDetails.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`admin-support-message ${
                    msg.is_internal
                      ? "admin-support-message--internal"
                      : msg.is_from_admin
                        ? "admin-support-message--admin"
                        : "admin-support-message--user"
                  }`}
                >
                  <div className="admin-support-message__header">
                    <span className="admin-support-message__author">
                      {msg.is_internal
                        ? "Internal Note"
                        : msg.is_from_admin
                          ? "Admin"
                          : "User"}
                    </span>
                    <span className="admin-support-message__time">
                      {new Date(msg.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="admin-support-message__content">{msg.message}</div>
                </div>
              ))}
            </div>

            <form className="admin-support-reply-form" onSubmit={handleReply}>
              <h4>Reply to Ticket</h4>
              <label>
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
                Internal note (not visible to user)
              </label>
              <label>
                Message
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  rows={4}
                  placeholder="Type your reply..."
                  required
                />
              </label>
              <Button type="submit" variant="primary" disabled={replyMutation.isPending}>
                {replyMutation.isPending ? "Sending..." : "Send Reply"}
              </Button>
            </form>

            <form className="admin-support-status-form" onSubmit={handleStatusUpdate}>
              <h4>Update Status</h4>
              <label>
                Status
                <select
                  value={statusUpdate.status}
                  onChange={(e) => setStatusUpdate({ ...statusUpdate, status: e.target.value })}
                  required
                >
                  <option value="">Select status</option>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </label>
              <label>
                Priority (optional)
                <select
                  value={statusUpdate.priority || ""}
                  onChange={(e) =>
                    setStatusUpdate({ ...statusUpdate, priority: e.target.value || undefined })
                  }
                >
                  <option value="">Keep current</option>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
              <Button type="submit" variant="primary" disabled={updateStatusMutation.isPending}>
                {updateStatusMutation.isPending ? "Updating..." : "Update Status"}
              </Button>
            </form>
          </div>
        </Card>
      )}
    </div>
  );
}

